// Authentication Store using Zustand

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { authApi, type AuthResponse } from '@api/auth.api';
import type { ClientRegistrationInput, ClientLoginInput } from '@phoenixbill/shared';

interface User {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
  type: 'client' | 'admin' | 'contact';
}

interface AuthState {
  // State
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;

  // Actions
  register: (data: ClientRegistrationInput) => Promise<void>;
  login: (data: ClientLoginInput) => Promise<void>;
  loginAdmin: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  verifyToken: () => Promise<boolean>;
  clearError: () => void;
  setLoading: (loading: boolean) => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      // Initial state
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,

      // Register
      register: async (data: ClientRegistrationInput) => {
        try {
          set({ isLoading: true, error: null });

          const response = await authApi.register(data);

          // Store token and user
          localStorage.setItem('token', response.token);

          set({
            user: response.user,
            token: response.token,
            isAuthenticated: true,
            isLoading: false,
            error: null,
          });
        } catch (error: any) {
          set({
            error: error.message || 'Registration failed',
            isLoading: false,
          });
          throw error;
        }
      },

      // Login
      login: async (data: ClientLoginInput) => {
        try {
          set({ isLoading: true, error: null });

          const response = await authApi.login(data);

          // Store token
          localStorage.setItem('token', response.token);

          set({
            user: response.user,
            token: response.token,
            isAuthenticated: true,
            isLoading: false,
            error: null,
          });
        } catch (error: any) {
          set({
            error: error.message || 'Login failed',
            isLoading: false,
          });
          throw error;
        }
      },

      // Login Admin
      loginAdmin: async (username: string, password: string) => {
        try {
          set({ isLoading: true, error: null });

          const response = await authApi.loginAdmin({ username, password });

          // Store token and user
          localStorage.setItem('token', response.token);
          localStorage.setItem('user', JSON.stringify(response.user));

          set({
            user: response.user,
            token: response.token,
            isAuthenticated: true,
            isLoading: false,
            error: null,
          });
        } catch (error: any) {
          set({
            error: error.message || 'Admin login failed',
            isLoading: false,
          });
          throw error;
        }
      },

      // Logout
      logout: async () => {
        try {
          await authApi.logout();
        } catch (error) {
          // Ignore logout errors
        } finally {
          // Clear local storage
          localStorage.removeItem('token');
          localStorage.removeItem('user');

          // Reset state
          set({
            user: null,
            token: null,
            isAuthenticated: false,
            error: null,
          });
        }
      },

      // Verify token
      verifyToken: async () => {
        const token = localStorage.getItem('token');

        if (!token) {
          set({
            user: null,
            token: null,
            isAuthenticated: false,
          });
          return false;
        }

        try {
          const response = await authApi.verify();

          // Get user from localStorage or from verify response
          const storedUser = localStorage.getItem('user');
          const user = storedUser ? JSON.parse(storedUser) : response.user;

          set({
            user,
            token,
            isAuthenticated: true,
          });

          return true;
        } catch (error) {
          // Token is invalid
          localStorage.removeItem('token');
          localStorage.removeItem('user');

          set({
            user: null,
            token: null,
            isAuthenticated: false,
          });

          return false;
        }
      },

      // Clear error
      clearError: () => {
        set({ error: null });
      },

      // Set loading
      setLoading: (loading: boolean) => {
        set({ isLoading: loading });
      },
    }),
    {
      name: 'auth-storage',
      partialize: state => ({
        user: state.user,
        token: state.token,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);
